/**
 * The four states every async region must render: loading, error, empty, data.
 *
 * `DataState` is the enforcement point — hand it a react-query result and it
 * picks the right one, so no page can accidentally ship a blank div while a
 * request is in flight or swallow a failure silently. Prefer a `skeleton`
 * shaped like the real content over a spinner: it keeps the layout still.
 */
import type { ReactNode } from 'react';
import { cx } from '@/lib/cx';
import { Icon, type IconName } from '../icons';
import { Button } from './Button';

// ------------------------------------------------------------------ spinner
export function Spinner({ label, size }: { label?: string; size?: 'sm' | 'lg' }) {
  return (
    <div className="loading-row" role="status">
      <span className={cx('spinner', size)} aria-hidden />
      <span>{label ?? 'Loading…'}</span>
    </div>
  );
}

// ----------------------------------------------------------------- skeleton
export function Skeleton({ variant = 'line', width, height, className }: {
  variant?: 'line' | 'title' | 'chip' | 'block';
  width?: number | string;
  height?: number | string;
  className?: string;
}) {
  return <span className={cx('skeleton', variant, className)} style={{ width, height }} aria-hidden />;
}

/** N shimmering lines — the default stand-in for a list or a paragraph. */
export function SkeletonRows({ rows = 4, className }: { rows?: number; className?: string }) {
  return (
    <div className={cx('skeleton-rows', className)} aria-hidden>
      {Array.from({ length: rows }, (_, i) => (
        <Skeleton key={i} width={i % 3 === 2 ? '62%' : i % 2 ? '88%' : '100%'} />
      ))}
    </div>
  );
}

/** A table-shaped placeholder so rows don't jump in when data lands. */
export function SkeletonTable({ rows = 5, cols = 3 }: { rows?: number; cols?: number }) {
  return (
    <div className="card-pad" aria-hidden>
      {Array.from({ length: rows }, (_, r) => (
        <div className="row gap-4" key={r} style={{ padding: 'var(--s2) 0' }}>
          {Array.from({ length: cols }, (_, c) => (
            <Skeleton key={c} className="grow" width={c === 0 ? '38%' : undefined} />
          ))}
        </div>
      ))}
    </div>
  );
}

// -------------------------------------------------------------------- empty
export function EmptyState({ title, hint, action, icon = 'Search', compact }: {
  title: string;
  hint?: ReactNode;
  action?: ReactNode;
  /** Pick an icon that describes the *missing* thing, not a generic box. */
  icon?: IconName;
  compact?: boolean;
}) {
  const Glyph = Icon[icon];
  return (
    <div className={cx('empty', compact && 'compact')}>
      <Glyph className="glyph" aria-hidden />
      <div className="stack gap-1 items-center">
        <span className="empty-title">{title}</span>
        {hint && <span className="empty-hint">{hint}</span>}
      </div>
      {action}
    </div>
  );
}

// -------------------------------------------------------------------- error
export function ErrorState({ title = 'Could not load', message, onRetry, detail }: {
  title?: string;
  message?: ReactNode;
  onRetry?: () => void;
  /** A correlation id or status code — what support will actually ask for. */
  detail?: ReactNode;
}) {
  return (
    <div className="empty danger" role="alert">
      <Icon.Alert className="glyph" aria-hidden />
      <div className="stack gap-1 items-center">
        <span className="empty-title">{title}</span>
        {message && <span className="empty-hint">{message}</span>}
        {detail && <span className="t-xs mono muted-more">{detail}</span>}
      </div>
      {onRetry && (
        <Button variant="secondary" size="sm" onClick={onRetry}>
          <Icon.Refresh aria-hidden /> Try again
        </Button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- DataState
export interface QueryLike<T> {
  isLoading: boolean;
  isError: boolean;
  error?: unknown;
  data?: T;
  isFetching?: boolean;
  refetch?: () => unknown;
}

export function DataState<T>({ query, empty, skeleton, loadingLabel, children }: {
  query: QueryLike<T>;
  empty?: { when: (d: T) => boolean; node: ReactNode };
  /** Rendered instead of the spinner while loading — prefer this. */
  skeleton?: ReactNode;
  loadingLabel?: string;
  children: (data: T) => ReactNode;
}) {
  if (query.isLoading) return <>{skeleton ?? <Spinner label={loadingLabel} />}</>;
  if (query.isError) {
    const msg = query.error instanceof Error ? query.error.message : 'Something went wrong.';
    return <ErrorState message={msg} onRetry={query.refetch ? () => query.refetch?.() : undefined} />;
  }
  if (query.data === undefined) return <>{skeleton ?? <Spinner label={loadingLabel} />}</>;
  if (empty && empty.when(query.data)) return <>{empty.node}</>;
  return <>{children(query.data)}</>;
}

// ----------------------------------------------------------------- progress
export function Progress({ value, label, indeterminate }: {
  /** 0–100. Omit (or pass `indeterminate`) when the total is unknown. */
  value?: number;
  label?: ReactNode;
  indeterminate?: boolean;
}) {
  const pct = Math.max(0, Math.min(100, value ?? 0));
  const unknown = indeterminate || value === undefined;
  return (
    <div className="stack gap-2 w-full">
      {label && <div className="stage">{label}</div>}
      <div
        className={cx('progress', unknown && 'indeterminate')}
        role="progressbar"
        aria-valuenow={unknown ? undefined : Math.round(pct)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={typeof label === 'string' ? label : 'Progress'}
      >
        <i style={unknown ? undefined : { width: `${pct}%` }} />
      </div>
    </div>
  );
}

// ------------------------------------------------------------------- notice
export function Notice({ tone = 'info', title, children, actions, icon }: {
  tone?: 'info' | 'ok' | 'warn' | 'danger';
  title?: ReactNode;
  children?: ReactNode;
  actions?: ReactNode;
  icon?: IconName;
}) {
  const fallback: Record<string, IconName> = { info: 'Info', ok: 'Check', warn: 'Alert', danger: 'Alert' };
  const Glyph = Icon[icon ?? fallback[tone] ?? 'Info'];
  return (
    <div className={cx('notice', tone)} role={tone === 'danger' ? 'alert' : undefined}>
      <Glyph aria-hidden />
      <div className="stack gap-1 grow">
        {title && <span className="notice-title">{title}</span>}
        {children}
        {actions && <div className="row gap-2 mt-1">{actions}</div>}
      </div>
    </div>
  );
}

// --------------------------------------------------------------- status dot
export function StatusDot({ state, label }: { state: 'up' | 'degraded' | 'down' | 'unknown'; label?: ReactNode }) {
  return (
    <span className="row gap-2">
      <span className={cx('health-dot', state !== 'unknown' && state)} aria-hidden />
      {label && <span>{label}</span>}
      <span className="sr-only">{state}</span>
    </span>
  );
}
